/** Sticky lock for soft angle snap across drag frames. */
export type StickyAngleSnapState = {
  /** Locked absolute angle in degrees, when engaged. */
  lockedDeg?: number;
};

export type SoftSnapDegreesToStepResult = {
  degrees: number;
  snapped: boolean;
  state: StickyAngleSnapState;
};

type SoftSnapDegreesToStepOptions = {
  enterDeg: number;
  breakoutDeg: number;
  sticky?: StickyAngleSnapState;
};

/**
 * Soft-snap {@link degrees} onto the nearest multiple of {@link stepDeg}.
 * Once locked, requires {@link SoftSnapDegreesToStepOptions.breakoutDeg}
 * of angular travel before leaving that step (Figma-like hysteresis).
 */
export const softSnapDegreesToStep = (
  degrees: number,
  stepDeg: number,
  options: SoftSnapDegreesToStepOptions,
): SoftSnapDegreesToStepResult => {
  const { enterDeg, breakoutDeg, sticky } = options;
  if (!(stepDeg > 0) || !Number.isFinite(stepDeg)) {
    return { degrees, snapped: false, state: {} };
  }
  if (!(enterDeg > 0) || !Number.isFinite(enterDeg)) {
    return { degrees, snapped: false, state: {} };
  }

  const locked = sticky?.lockedDeg;
  if (locked !== undefined) {
    if (Math.abs(degrees - locked) <= breakoutDeg) {
      return {
        degrees: locked,
        snapped: true,
        state: { lockedDeg: locked },
      };
    }
  }

  const nearest = Math.round(degrees / stepDeg) * stepDeg;
  if (Math.abs(degrees - nearest) > enterDeg) {
    return { degrees, snapped: false, state: {} };
  }

  return {
    degrees: nearest,
    snapped: true,
    state: { lockedDeg: nearest },
  };
};
